import { Box, Dialog, Typography, useTheme } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { useTranslation } from "react-i18next";

// Series colors: validated pair (green = terrain, blue = pipe invert), stepped
// per mode so both clear 3:1 on the respective surface.
const SERIES_COLORS = {
  light: { ground: "#008300", invert: "#2a78d6" },
  dark: { ground: "#008300", invert: "#3987e5" },
};

// pressure pipe highlight: amber band + border, distinct from the green/blue
// series so a pressurized (pumped) segment reads at a glance in both modes.
const PRESSURE = {
  light: { fill: "rgba(180, 83, 9, 0.12)", stroke: "#b45309" },
  dark: { fill: "rgba(245, 158, 11, 0.16)", stroke: "#f59e0b" },
};

// chart chrome (ink/grid/surface) per mode
const CHROME = {
  light: {
    ink: "#52514e",
    muted: "#898781",
    grid: "#e1e0d9",
    axis: "#c3c2b7",
    surface: "#fcfcfb",
    tooltipInk: "#0b0b0b",
  },
  dark: {
    ink: "#c3c2b7",
    muted: "#898781",
    grid: "#2c2c2a",
    axis: "#383835",
    surface: "#1a1a19",
    tooltipInk: "#ffffff",
  },
};

export const ElevationProfileDialog = ({ data, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode === "dark" ? "dark" : "light";
  const seriesColors = SERIES_COLORS[mode];
  const chrome = CHROME[mode];
  const pressure = PRESSURE[mode];

  if (!data) return null;

  const groundLabel = t("Ground Elevation");
  const invertLabel = t("Invert Elevation");

  const series = [
    // invert first so the ground line draws on top at crossings
    ...(data.invert
      ? [{ id: invertLabel, color: seriesColors.invert, data: data.invert }]
      : []),
    { id: groundLabel, color: seriesColors.ground, data: data.ground },
  ];

  // Custom layer: highlight pressure-pipe segments with an amber band spanning
  // the distance range of each pumped edge along the profile.
  const pressurePipesLayer = ({ xScale, innerHeight }) => {
    if (!data.pressureSegments || data.pressureSegments.length === 0)
      return null;
    return (
      <g>
        {data.pressureSegments.map((seg, idx) => {
          const x1 = xScale(seg.x1);
          const x2 = xScale(seg.x2);
          return (
            <g key={`pressure-${idx}`}>
              <rect
                x={x1}
                y={0}
                width={Math.max(x2 - x1, 0)}
                height={innerHeight}
                fill={pressure.fill}
              />
              <line
                x1={x1}
                x2={x2}
                y1={0}
                y2={0}
                stroke={pressure.stroke}
                strokeWidth={3}
              />
            </g>
          );
        })}
      </g>
    );
  };

  // Custom layer: flag pump (lift) stations with a symbol + guide line at the
  // node's position along the profile.
  const pumpMarkersLayer = ({ xScale, innerHeight }) => {
    if (!data.pumps || data.pumps.length === 0) return null;
    const size = 22;
    return (
      <g>
        {data.pumps.map((pump) => {
          const cx = xScale(pump.x);
          return (
            <g key={`pump-${pump.name}-${pump.x}`}>
              <line
                x1={cx}
                x2={cx}
                y1={0}
                y2={innerHeight}
                stroke={chrome.muted}
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              <image
                href="/assets/svg/map/pump.svg"
                x={cx - size / 2}
                y={2}
                width={size}
                height={size}
              >
                <title>{`${t("Pump station")}: ${pump.name}`}</title>
              </image>
            </g>
          );
        })}
      </g>
    );
  };

  const nivoTheme = {
    background: "transparent",
    text: { fill: chrome.ink, fontSize: 11 },
    axis: {
      domain: { line: { stroke: chrome.axis, strokeWidth: 1 } },
      legend: { text: { fontSize: 12, fill: chrome.ink } },
      ticks: {
        line: { stroke: chrome.axis, strokeWidth: 1 },
        text: { fontSize: 11, fill: chrome.muted },
      },
    },
    grid: { line: { stroke: chrome.grid, strokeWidth: 1 } },
    legends: { text: { fontSize: 11, fill: chrome.ink } },
    crosshair: { line: { stroke: chrome.muted, strokeWidth: 1 } },
    tooltip: {
      container: {
        background: chrome.surface,
        color: chrome.tooltipInk,
        fontSize: 12,
      },
    },
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <Box className="flex flex-col p-4 gap-1">
        <Typography variant="h4">{t("Elevation profile")}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {`${t("Node")} ${data.start.name} → ${t("Node")} ${data.end.name} · ${t(
            "Total length"
          )}: ${data.totalLength} m`}
        </Typography>
        <Box sx={{ height: "400px" }}>
          <ResponsiveLine
            data={series}
            theme={nivoTheme}
            colors={(serie) => serie.color}
            margin={{ top: 40, right: 30, bottom: 55, left: 60 }}
            xScale={{ type: "linear", min: "auto", max: "auto" }}
            yScale={{ type: "linear", min: "auto", max: "auto" }}
            curve="linear"
            layers={[
              "grid",
              pressurePipesLayer,
              "markers",
              "axes",
              "areas",
              "crosshair",
              "lines",
              "slices",
              "points",
              pumpMarkersLayer,
              "mesh",
              "legends",
            ]}
            lineWidth={2}
            pointSize={8}
            pointBorderWidth={2}
            pointBorderColor={chrome.surface}
            pointColor={{ from: "serieColor" }}
            enableGridX={false}
            axisBottom={{
              legend: t("Distance (m)"),
              legendOffset: 42,
              legendPosition: "middle",
              tickSize: 4,
            }}
            axisLeft={{
              legend: t("Elevation (m)"),
              legendOffset: -50,
              legendPosition: "middle",
              tickSize: 4,
            }}
            enableSlices="x"
            sliceTooltip={({ slice }) => (
              <Box
                sx={{
                  background: chrome.surface,
                  color: chrome.tooltipInk,
                  padding: "6px 10px",
                  borderRadius: "4px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                  fontSize: "12px",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                  {`${t("Node")} ${slice.points[0].data.name} · ${
                    slice.points[0].data.x
                  } m`}
                </Typography>
                {slice.points.map((point) => (
                  <Box
                    key={point.id}
                    className="flex items-center gap-2"
                    sx={{ marginTop: "2px" }}
                  >
                    <Box
                      sx={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "2px",
                        backgroundColor: point.serieColor,
                      }}
                    />
                    <span>{`${point.serieId}: ${Number(
                      point.data.y
                    ).toFixed(2)} m`}</span>
                  </Box>
                ))}
              </Box>
            )}
            legends={[
              {
                anchor: "top-left",
                direction: "row",
                translateY: -30,
                itemWidth: 140,
                itemHeight: 18,
                symbolSize: 10,
                symbolShape: "circle",
              },
            ]}
          />
        </Box>
        {(data.pumps?.length > 0 || data.pressureSegments?.length > 0) && (
          <Box className="flex items-center gap-4 px-2" sx={{ opacity: 0.85 }}>
            {data.pumps?.length > 0 && (
              <Box className="flex items-center gap-1">
                <img
                  src="/assets/svg/map/pump.svg"
                  alt=""
                  width={16}
                  height={16}
                />
                <Typography variant="caption">{t("Pump station")}</Typography>
              </Box>
            )}
            {data.pressureSegments?.length > 0 && (
              <Box className="flex items-center gap-1">
                <Box
                  sx={{
                    width: "16px",
                    height: "12px",
                    borderRadius: "2px",
                    backgroundColor: pressure.fill,
                    borderTop: `3px solid ${pressure.stroke}`,
                  }}
                />
                <Typography variant="caption">{t("Pressure pipe")}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
